"use client";

import React, { useEffect, useState } from 'react';
import { ShortsAndReels as ShortsAndReelsLib } from "@farm-junction/design-library";
import { CDN_URL } from "@/app/utilities/constants";
import CustomSlider from "@/component/ui/CustomSlider/CustomSlider";
import { CAROUSEL_SLIDER_PAD } from "@/component/ui/CarouselViewAll";
import Heading from "@/component/ui/Heading";

interface Video {
  id?: string | number;
  title?: string;
  description?: string;
  shortUrl?: string;
  thumbnailUrl?: string;
  video_url?: string;
  thumbnail_url?: string;
  custom_thumbnail_url?: string;
  duration_seconds?: number;
}

interface ShortsAndReelsProps {
  videos: Video[];
  isMobile?: boolean;
  heading?: string;
  itemsToShow?: number;
}

// YouTube embed origins — dns-prefetch only (site layout already uses preconnect budget).
const YOUTUBE_DNS_PREFETCH_ORIGINS = [
  "https://www.youtube.com",
  "https://i.ytimg.com",
  "https://s.ytimg.com",
  "https://www.google.com",
];

const escapeHtml = (value: string) =>
  value.replace(/[&<>"']/g, (char) => (
    { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[char] as string
  ));

/**
 * The library renders the reel overlay as an empty div and exposes no slot for a header, so the
 * title/channel row and the centre play icon are injected through that div's HTML.
 * The play icon is shown only while the poster is still on screen (`.reel-poster` is the poster
 * marker passed via customPosterProps), i.e. until the video has actually started.
 */
const buildReelOverlayHtml = (title: string) => `
<div class="pointer-events-none absolute inset-x-0 top-0 flex items-center gap-2 bg-linear-to-b from-black/70 to-transparent pb-6 pl-14 pr-3 pt-3 md:pl-3">
  <img src="/images/car-junction-logo.svg" alt="" width="28" height="28" class="size-7 shrink-0 rounded-full bg-white object-contain p-[3px]" />
  <div class="min-w-0">
    <p class="truncate text-[14px] font-bold leading-[18px] text-white">${escapeHtml(title)}</p>
    <p class="truncate text-[12px] leading-[16px] text-white/80">Car Junction</p>
  </div>
</div>
<div class="pointer-events-none absolute inset-0 hidden items-center justify-center group-has-[.reel-poster]/reel:flex">
  <span class="flex size-14 items-center justify-center rounded-full bg-black/55 backdrop-blur-sm">
    <svg viewBox="0 0 24 24" aria-hidden="true" class="ml-0.5 size-6 fill-white"><path d="M8 5v14l11-7z"/></svg>
  </span>
</div>`;

const REEL_FRAME_SELECTOR = ".group\\/reel";
const POLL_INTERVAL_MS = 300;
const POSTER_HIDE_AFTER_LOAD_MS = 1000;
const POSTER_HIDE_MAX_WAIT_MS = 4000;

interface PlaybackSession {
  src: string;
  startedAt: number;
  loadedAt: number | null;
}

const ShortsAndReels: React.FC<ShortsAndReelsProps> = ({ 
  videos = [], 
  isMobile = false,
  heading = "Featured Videos",
  itemsToShow = 5,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [posterHidden, setPosterHidden] = useState(false);

  useEffect(() => {
    const links = YOUTUBE_DNS_PREFETCH_ORIGINS.map((origin) => {
      const link = document.createElement("link");
      link.rel = "dns-prefetch";
      link.href = origin;
      document.head.appendChild(link);
      return link;
    });
    return () => {
      links.forEach((link) => link.remove());
    };
  }, []);

  // The library removes the poster only when the YouTube player reports "playing" back via
  // postMessage. That message often never arrives, so the video (and its sound) plays underneath
  // a poster that never goes away. Once the player has loaded, drop the poster ourselves; the
  // embed itself is left untouched so playback and sound are not interrupted.
  useEffect(() => {
    let session: PlaybackSession | null = null;

    const timer = window.setInterval(() => {
      const frame = document.querySelector<HTMLElement>(REEL_FRAME_SELECTOR);
      const iframe = frame?.querySelector("iframe");

      if (!frame || !iframe) {
        if (session) {
          session = null;
          setPosterHidden(false);
        }
        return;
      }

      const now = Date.now();

      if (!session || session.src !== iframe.src) {
        const current: PlaybackSession = { src: iframe.src, startedAt: now, loadedAt: null };
        iframe.addEventListener("load", () => { current.loadedAt = Date.now(); }, { once: true });
        session = current;
        setPosterHidden(false);
        return;
      }

      if (!frame.querySelector(".reel-poster")) return;

      const loaded = session.loadedAt !== null && now - session.loadedAt >= POSTER_HIDE_AFTER_LOAD_MS;
      const waitedTooLong = now - session.startedAt >= POSTER_HIDE_MAX_WAIT_MS;
      if (loaded || waitedTooLong) setPosterHidden(true);
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, []);

  const handleCurrentIndexChange = (index: number) => {
    setCurrentIndex(index);
  };

  const handleOfferClick = () => {
    console.log('Offer clicked for video:', videos[currentIndex]?.title);
  };

  if (!videos || videos.length === 0) {
    return null;
  }

  // Transform videos to match library's expected format
  const transformedVideos = videos.map((video, index) => ({
    id: video.id || index,
    title: video.title || '',
    description: video.description || '',
    shortUrl: video.shortUrl || video.video_url || '',
    thumbnailUrl: video.custom_thumbnail_url
      ? CDN_URL + video.custom_thumbnail_url
      : video.thumbnailUrl || video.thumbnail_url || '',
    duration_seconds: video.duration_seconds || 0,
  }));

  return (
    <div className="font-sans">
      <Heading tag="h2" className="font-bold mb-4">
        {heading}
      </Heading>
      <ShortsAndReelsLib
        renderThumbnails={(_videos, _openReel, thumbnails) => (
          <div className={CAROUSEL_SLIDER_PAD}>
            <CustomSlider
              itemsToShow={isMobile ? 2.2 : itemsToShow}
              responsive={{ mobile: 2.2, tablet: 3, desktop: itemsToShow }}
              showArrows
              showDots={true}
              gap={16}
            >
              {thumbnails}
            </CustomSlider>
          </div>
        )}
        className="max-w-[280px] relative overflow-hidden rounded-xl before:content-[''] before:absolute before:bottom-0 before:left-0 before:w-full before:h-1/2 before:bg-linear-to-t before:from-black/95 before:to-transparent before:rounded-b-xl"
        icon="fillUpCaret"
        onCurrentIndexChange={handleCurrentIndexChange}
        slotProps={{
          headingProps: {
            className: "text-[13px] leading-[18px] md:text-[14px] md:leading-[19px] font-bold text-[#ffffff] line-clamp-3",
            level: "p",
            variant: "p",
          },
          iconProps: {
            className: "w-[13px] h-[10px] rotate-90 playback_started",
            fill: "#000",
          },
          // 9:17 matches the Featured Videos cards, so the centre of the 16:9 thumbnail (the
          // actual vertical video) fills the card and the thumbnail's side bars are cropped away.
          imageProps: {
            className: "block w-full h-auto aspect-[9/17] object-cover rounded-xl",
          },
          textProps: {
            className: "text-[14px] text-[#E5E7EB]",
          },
          iconWrapperProps: {
            className: "w-10 h-10 shadow-md",
          },
          modalPortalProps: {
            className: "z-[999] !bg-[#000000D9]",
          },
          // Mobile: back arrow inside the frame (top-left). Desktop: above the frame, at its top-left.
          modalCloseProps: {
            className: "px-1 player_closed !z-[99] left-3 top-3 rounded-full bg-black/40 hover:bg-black/60 md:-top-0 md:left-0 md:bg-transparent md:hover:bg-transparent",
          },
          modalWrapperProps: {
            className:
              "w-full max-w-full overflow-hidden reel_displayed px-[15px] py-0 md:max-w-[390px] md:overflow-visible md:px-0",
          },
          modalProps: {
            className:
              "h-auto w-fit max-w-full p-0 bg-transparent reel_clicked md:max-w-[390px]",
          },
          // 9:16 frame. Mobile: inset 15px from each side and centred like the Bike Junction popup.
          // Desktop: up to 390x693, shrinking with the viewport height. `group/reel` lets the
          // injected play icon react to the poster being present.
          modalBodyProps: {
            className:
              "group/reel h-[min(calc((100vw_-_30px)*1.7778),calc(100dvh_-_7rem))] w-auto aspect-[9/16] rounded-xl md:h-[min(693px,calc(100dvh-4rem))] md:rounded-2xl",
          },
          // The library spreads this prop after its own className, so the full class list is needed.
          customPosterProps: {
            className: posterHidden
              ? "hidden"
              : "reel-poster absolute inset-0 z-[2] w-full h-full object-cover rounded pointer-events-none",
          },
          topOverlyCls: {
            className: "w-full h-full",
            dangerouslySetInnerHTML: {
              __html: buildReelOverlayHtml(videos[currentIndex]?.title ?? ""),
            },
          },
          shareButtonProps: {
            className: "bottom-14",
          },
          // Previous/next arrows: library default (visible on mobile and desktop).
          arrowsProps: {
            className: "z-[99]",
          },
          shareModalPortalProps: {
            className: "z-[9999]",
          },
          shareWrapperProps: {
            className: "sa2",
          },
          shareModalProps: {
            className: "max-w-[400px] mx-auto",
          },
          shareModalCloseProps: {
            className: "px-[10px]",
          },
          nextButtonProps: {
            className: "next_reel_viewed",
          },
          prevButtonProps: {
            className: "prev_reel_viewed",
          },
          staticAdButtonProps: {
            className: "w-full bg-[#e77b35] hover:bg-[#eb630b]",
          },
          staticAdButtonWrapperProps: {
            className:
              "absolute inset-x-0 bottom-1 z-20 flex justify-center px-3 pointer-events-auto py-3",
          },
          customShareButtonProps: {
            className: "absolute bottom-[88px] right-3 z-[70] bg-[#000000D9]",
          }
        }}
        customShare={true}
        videos={transformedVideos}
        staticAd={true}
        staticAdButtonText="Get Offer"
        handleOfferClick={handleOfferClick}
      />
    </div>
  );
};

export default ShortsAndReels;